import { Alert, Group, type MantineSpacing, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useState } from 'react';
import { Button } from 'src/design-system/Button';
import { useFlags } from 'src/feature-flags/FlagsHook';
import { CalendarGuestChangesModal } from 'src/meetings/detail/components/CalendarGuestChangesModal';
import type { CalendarEvent } from 'src/meetings/models';
import type { MeetingScope } from 'src/meetings/scope';

type Props = {
  event: CalendarEvent;
  scope?: MeetingScope;
  mt?: MantineSpacing;
  mb?: MantineSpacing;
};

export function CalendarGuestChangesBanner({ event, scope, mt, mb }: Props) {
  const { flagEnabledMeetingsRework } = useFlags();
  const [opened, setOpened] = useState(false);
  const visible = Boolean(flagEnabledMeetingsRework && event.attendeeDifferences?.length);

  // Hiding the banner unmounts the review without closing it, so a later guest change would reopen it unasked.
  if (opened && !visible) setOpened(false);

  if (!visible) {
    return null;
  }

  return (
    <>
      <Alert
        variant="light"
        color="yellow"
        icon={<IconAlertTriangle size={18} />}
        py="xs"
        mt={mt}
        mb={mb}
        styles={{ wrapper: { alignItems: 'center' } }}
      >
        <Group justify="space-between" align="center" wrap="nowrap" gap="md">
          <Text size="sm">
            {
              "The calendar's guest list changed, and the attendees were updated to match. Review the changes."
            }
          </Text>
          <Button variant="text" onClick={() => setOpened(true)}>
            Review
          </Button>
        </Group>
      </Alert>
      <CalendarGuestChangesModal
        opened={opened}
        onClose={() => setOpened(false)}
        event={event}
        scope={scope}
      />
    </>
  );
}
